import {
  CaseAssignmentResponse,
  ICaseAssignmentBodyReq,
  IDataAPICaseStatus
} from "@/types/casesFrontendTypes";
import { IDataAPIUser } from "@/types/users";
import { isValidData, truthyFilter } from "./functions";

export type UserDisplay = {
  id: string;
  name: string;
};

export type IClosedCaseStatusData = Record<keyof IDataAPICaseStatus, string>;
export type CaseAssigneeData =
  | CaseAssigneeChoice
  | CaseAssigneeOptions
  | CaseAssigneeList;
export type CaseAssigneeChoice = Record<
  "choice",
  CaseAssignmentResponse[] | null
>; // /api/cases/{case_id}/assignment
export type CaseAssigneeOptions = Record<"options", IDataAPIUser[] | null>; // /users/assignment-options
export type CaseAssigneeList = Record<"list", CaseAssignmentResponse[] | null>; // /api/cases/{case_id}/visibility

/**
 * Translates the received data to a common format (of type UserDisplay)
 * and merges them into one array which gets returned
 * @param assignedBySelf data of assignees set by the current user
 * @param assignedByOthers data of assignees set by other users
 */
export function getClosedCaseAssigneeData(
  assignedBySelf: CaseAssignmentResponse[] | null,
  assignedByOthers: CaseAssignmentResponse[] | null
): UserDisplay[] {
  if (isValidData(assignedBySelf) && isValidData(assignedByOthers)) {
    // returning the combination of both translated data
    const filteredAssignees = [...assignedBySelf, ...assignedByOthers]
      .map((item): UserDisplay | null => {
        const { id, displayName } = item.profile;
        return id && displayName ? { id: id, name: displayName } : null;
      })
      .filter(isValidData);
    return [
      ...new Map(filteredAssignees.map((item) => [item.id, item])).values()
    ];
  } else {
    return [];
  }
}

/**
 * Return the parsed & formatted version
 * of the argument, ready for display in the
 * closed-case component
 * @param data The original form of the status-data
 */
export function getClosedCaseStatusData(
  data: IDataAPICaseStatus | null
): IClosedCaseStatusData {
  const _default: IClosedCaseStatusData = {
    status: "-",
    additional_comments: "-",
    adjusted_reason_code: "-",
    case_id: "-",
    modified_by: "-",
    result: "-",
    result_explanation: "-"
  };

  if (isValidData(data)) {
    // Extract desired values
    const { additional_comments, result, result_explanation, status } = data;

    // Use the extracted-above to override the relevant prop fields
    return Object.assign({}, _default, {
      additional_comments: isValidData(additional_comments)
        ? additional_comments
        : _default.additional_comments,
      status: status,
      result: isValidData(result) ? result : _default.result,
      result_explanation: isValidData(result_explanation)
        ? result_explanation
        : _default.result_explanation
    });
  } else {
    return _default;
  }
}

/**
 * Returns the subset of the case-visibility list which:
 * 1. is an 'Editor' (i.e. has been assigned); AND
 * 2. is NOT an option selectable by the current user
 * @param list case-visibility list
 * @param options case-assignment user options
 */
export function getAssignmentDifference(
  list: CaseAssignmentResponse[] | null,
  options: IDataAPIUser[] | null
): CaseAssignmentResponse[] | null {
  if (Array.isArray(list) && Array.isArray(options)) {
    // Reference: https://stackoverflow.com/questions/1187518
    const arr1 = list.filter((item) => item.permission === "Editor");
    const arr2 = options.map((item) => item.id);
    return arr1.filter(
      (x) => truthyFilter(x.profile.id) && !arr2.includes(x.profile.id)
    );
  } else {
    return null;
  }
}

export function extractAssignmentRequestBody(
  data: CaseAssignmentResponse[]
): ICaseAssignmentBodyReq[] {
  return data
    .map((item): ICaseAssignmentBodyReq | null => {
      const {
        permission,
        profile: { id }
      } = item;
      return id && permission
        ? { assigned_to: id, permission: permission }
        : null;
    })
    .filter(isValidData);
}
